/**
 * Hook for checking specific user permissions
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthContext } from '@contexts/AuthContext';
import { useUserRole } from './useUserRole';
import permissionService from '@services/permissionService';

/**
 * Hook to check if current user has specific permission types
 * @param {Array} permissionTypes - Array of permission types to check
 * @returns {Object} Object with permission results, loading state, and error
 */
export const useSpecificPermissions = (permissionTypes = []) => {
  const { user } = useAuthContext();
  const { isSuperAdmin, isAdmin } = useUserRole();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkPermissions = useCallback(async () => {
    if (!user || !permissionTypes.length) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Super admin can do everything
      if (isSuperAdmin) {
        const allPermissions = {};
        permissionTypes.forEach(type => {
          allPermissions[type] = true;
        });
        setPermissions(allPermissions);
        setLoading(false);
        return;
      }

      // Admin has certain permissions by default
      const adminPermissions = {
        'RESOURCE_MANAGEMENT': isAdmin,
        'LOCATION_MANAGEMENT': false // Admins don't have this by default, need specific permission
      };

      // Check each permission
      const permissionChecks = await Promise.all(
        permissionTypes.map(async (type) => {
          // If admin has this permission by default, return true
          if (adminPermissions[type] === true) {
            return { type, hasPermission: true };
          }

          // Otherwise check with the API
          const hasPermission = await permissionService.checkPermission(type);
          return { type, hasPermission };
        })
      );

      const permissionResults = {};
      permissionChecks.forEach(({ type, hasPermission }) => {
        permissionResults[type] = hasPermission;
      });

      setPermissions(permissionResults);
    } catch (err) {
      console.error('Failed to check permissions:', err);
      setError(err);
      // Set all to false on error for security
      const errorPermissions = {};
      permissionTypes.forEach(type => {
        errorPermissions[type] = false;
      });
      setPermissions(errorPermissions);
    } finally {
      setLoading(false);
    }
  }, [user, isSuperAdmin, isAdmin, JSON.stringify(permissionTypes)]);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const refresh = useCallback(() => {
    checkPermissions();
  }, [checkPermissions]);

  return { permissions, loading, error, refresh };
};

/**
 * Hook to check if user can create shared resources
 * (needs ADMIN/SUPER_ADMIN role OR LOCATION_MANAGEMENT OR RESOURCE_MANAGEMENT permissions)
 */
export const useCanCreateSharedResources = () => {
  const { isSuperAdmin, isAdmin } = useUserRole();
  const { permissions, loading, error } = useSpecificPermissions(['LOCATION_MANAGEMENT', 'RESOURCE_MANAGEMENT']);

  const canCreateShared = useMemo(() => {
    // Super admin and admin can always create shared resources
    if (isSuperAdmin || isAdmin) {
      return true;
    }

    // Check if user has either permission
    return permissions['LOCATION_MANAGEMENT'] || permissions['RESOURCE_MANAGEMENT'];
  }, [isSuperAdmin, isAdmin, permissions]);

  return { canCreateShared, loading, error };
};

export default useSpecificPermissions;
